import { useState } from "react";
import { Check, Copy } from "lucide-react";

/*
 * Bloco monoespaçado com botão de copiar — para payload, resposta crua do
 * provider e `trace_id` (doc 06a §5).
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O CONTEÚDO É INSERIDO COMO TEXTO, NUNCA COMO HTML.                       │
 * │                                                                          │
 * │ O que entra aqui é payload de cliente e resposta de provider — entrada   │
 * │ de terceiro, que passou por um banco de dados. É exatamente a superfície │
 * │ que o doc 13 §10 manda olhar num security-review: XSS no que vem do      │
 * │ banco.                                                                   │
 * │                                                                          │
 * │ React escapa `{conteudo}` por padrão. Este comentário existe para que a  │
 * │ próxima pessoa que pensar em `dangerouslySetInnerHTML` aqui — para       │
 * │ colorir sintaxe, digamos — saiba que a escolha foi deliberada, e que a   │
 * │ CSP servida pelo nginx também não permitiria o script resultante.        │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function BlocoDeCodigo({
  rotulo,
  conteudo,
  vazio = "Nada registrado.",
}: {
  rotulo: string;
  /** Objeto (é serializado com recuo) ou texto cru. */
  conteudo: unknown;
  /** O que dizer quando não há conteúdo. Diga o que isso significa. */
  vazio?: string;
}) {
  const [copiado, setCopiado] = useState(false);
  const texto = serializar(conteudo);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      // `navigator.clipboard` exige contexto seguro e permissão. Falhar em
      // silêncio é certo: o texto continua na tela, selecionável.
    }
  }

  return (
    <section className="flex flex-col gap-1">
      <header className="flex items-center justify-between">
        <span className="rotulo">{rotulo}</span>
        {texto !== "" && (
          <button
            type="button"
            onClick={() => void copiar()}
            aria-label={`Copiar ${rotulo}`}
            className="rounded-controle p-1 text-texto-2 hover:bg-superficie-2 hover:text-texto"
          >
            {copiado ? (
              <Check aria-hidden="true" className="size-3.5 text-pago" />
            ) : (
              <Copy aria-hidden="true" className="size-3.5" />
            )}
          </button>
        )}
      </header>

      {texto === "" ? (
        <p className="text-[12px] text-texto-3">{vazio}</p>
      ) : (
        /*
          `overflow-x-auto` no bloco, e não quebra de linha: um JSON de payload
          com quebra automática vira um parágrafo ilegível, e o operador precisa
          ver a estrutura. A rolagem fica DENTRO do bloco — a página não rola na
          horizontal.
        */
        <pre className="dados max-h-[320px] overflow-auto rounded-controle border border-fio bg-superficie-2 p-3 text-[12px] leading-relaxed text-texto">
          {texto}
        </pre>
      )}
    </section>
  );
}

function serializar(conteudo: unknown): string {
  if (conteudo === null || conteudo === undefined) return "";
  if (typeof conteudo === "string") return conteudo;

  try {
    // `?? ""` porque `JSON.stringify` devolve `undefined` — e não a string
    // `"undefined"` — para função e `Symbol`. Sem isto o `pre` receberia
    // `undefined` e o bloco sumiria sem explicação.
    return JSON.stringify(conteudo, null, 2) ?? "";
  } catch {
    /*
     * Referência circular, `BigInt`. Não deveria acontecer com corpo vindo de
     * JSON, mas uma tela de diagnóstico que quebra ao exibir o dado é o oposto
     * do que ela existe para fazer.
     *
     * `String(objeto)` daria "[object Object]", que é pior que inútil: parece
     * conteúdo. A mensagem diz o que houve.
     */
    return "[o conteúdo não pôde ser serializado para exibição]";
  }
}
